export const highlightSearchTerm = (text, searchTerm) => {
  if (!searchTerm || !text || searchTerm.length < 3) return text;

  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts = text.toString().split(regex);
  return parts.map((part, index) =>
    regex.test(part) ? (
      <span key={index} style={{ backgroundColor: '#E3F2FD', color: '#1565C0', padding: '0 2px', borderRadius: '2px' }}>{part}</span>
    ) : (
      part
    )
  );
};

export const parseApiDate = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  if (typeof dateValue === 'string') {
    const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(dateValue);
    const isIsoDateTime = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:/.test(dateValue);
    const normalizedValue = isIsoDateTime && !hasTimezone ? `${dateValue.replace(' ', 'T')}Z` : dateValue;
    const parsedDate = new Date(normalizedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const parsedDate = new Date(dateValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getApiDateTimeMillis = (dateValue) => {
  const date = parseApiDate(dateValue);
  return date ? date.getTime() : 0;
};

export const formatApiDate = (dateValue, options = {}) => {
  const date = parseApiDate(dateValue);
  if (!date) return '-';

  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options,
  });
};

export const formatApiTime = (dateValue, options = {}) => {
  const date = parseApiDate(dateValue);
  if (!date) return '-';

  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options,
  });
};

export const formatApiDateTime = (dateValue, options = {}) => {
  const date = parseApiDate(dateValue);
  if (!date) return '-';

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options,
  });
};

export const todayDateInputValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toDateInputValue = (dateValue) => {
  if (!dateValue) return todayDateInputValue();

  if (typeof dateValue === 'string') {
    const match = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }

  const date = parseApiDate(dateValue);
  if (!date) return todayDateInputValue();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateInputParts = (dateValue) => {
  if (!dateValue) return null;
  const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]) - 1,
    day: Number(match[3]),
  };
};

const parseTimeInputParts = (timeValue, fallback) => {
  const value = timeValue || fallback;
  const match = String(value).match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  return {
    hours: Number(match[1]),
    minutes: Number(match[2]),
    seconds: Number(match[3] || 0),
  };
};

export const toUtcISOStringFromLocalInputs = (dateValue, timeValue, options = {}) => {
  const dateParts = parseDateInputParts(dateValue);
  if (!dateParts) return null;

  const timeParts = parseTimeInputParts(timeValue, options.fallbackTime || '00:00');
  if (!timeParts) return null;

  const date = new Date(
    dateParts.year,
    dateParts.month,
    dateParts.day,
    timeParts.hours,
    timeParts.minutes,
    timeParts.seconds,
    options.endOfDay ? 999 : 0
  );

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const buildUtcDateTimeFilterRange = ({ dateFrom, dateTo, timeFrom, timeTo }) => {
  const dateTimeFromUtc = dateFrom
    ? toUtcISOStringFromLocalInputs(dateFrom, timeFrom, { fallbackTime: '00:00' })
    : null;
  const dateTimeToUtc = dateTo
    ? toUtcISOStringFromLocalInputs(dateTo, timeTo, { fallbackTime: '23:59:59', endOfDay: true })
    : null;

  if (!dateTimeFromUtc && !dateTimeToUtc) {
    return { dateTimeFromUtc: null, dateTimeToUtc: null };
  }

  if (dateTimeFromUtc && dateTimeToUtc && new Date(dateTimeToUtc) < new Date(dateTimeFromUtc)) {
    const nextDayEnd = new Date(dateTimeToUtc);
    nextDayEnd.setDate(nextDayEnd.getDate() + 1);
    return { dateTimeFromUtc, dateTimeToUtc: nextDayEnd.toISOString() };
  }

  return { dateTimeFromUtc, dateTimeToUtc };
};

export const getClientTimezoneOffsetMinutes = () => new Date().getTimezoneOffset();

export const formatCalendarDate = (dateValue, options = {}) => {
  if (!dateValue) return '-';

  if (typeof dateValue === 'string') {
    const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        ...options,
      });
    }
  }

  return formatApiDate(dateValue, options);
};

export const formatRelativeTime = (dateString) => {
  const date = parseApiDate(dateString);
  if (!date) return '';

  const now = new Date();
  const diffMs = Math.max(0, now - date);
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
};

export const mapRoleIdToUserType = (roleId) => {
  if (!roleId) return null;

  switch (Number(roleId)) {
    case 2: return 'admin';
    case 3: return 'department';
    case 4: return 'caregiver';
    case 5: return 'security';
    case 6: return 'housekeeping';
    case 7: return 'f&b';
    default: return null;
  }
};
